import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

/** Flat settings shape (every key is stored as a string in AppSetting.value). */
export type TenantSettings = Record<string, string>;

export interface GreenApiTestResult {
  ok: boolean;
  status: number;
  data: unknown;
}

const settingsKey = (slug: string) => ["tenant", slug, "settings"] as const;

export const useTenantSettings = (slug: string | undefined) =>
  useQuery({
    queryKey: settingsKey(slug ?? ""),
    queryFn: async () =>
      (
        await axios.get<TenantSettings, AxiosResponse<TenantSettings>>(
          `/t/${slug}/settings`,
        )
      ).data,
    enabled: !!slug,
  });

export const useUpdateTenantSettings = (slug: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (
        await axios.patch<
          { ok: boolean; updated: number },
          AxiosResponse<{ ok: boolean; updated: number }>
        >(`/t/${slug}/settings`, patch)
      ).data,
    onSuccess: () => {
      if (slug) void qc.invalidateQueries({ queryKey: settingsKey(slug) });
    },
  });
};

export const useTestGreenApi = (slug: string | undefined) =>
  useMutation({
    mutationFn: async () =>
      (
        await axios.post<GreenApiTestResult, AxiosResponse<GreenApiTestResult>>(
          `/t/${slug}/settings/test-green-api`,
        )
      ).data,
  });
